import type * as unist from 'unist';

import { Block, type Document, type Fragment, Inline, type Text } from '@gitbook/slate';
import { assertNever } from '@gitbook/util-ts';

import type { FormattingContext } from '../context';

/**
 * Function to transform a slate node into unist ones.
 */
export type GastToAstTransformFn<
    NodeType extends Block | Inline | Text = Block | Inline | Text,
    Result = unist.Node,
    ExpectedChildren = unist.Node,
    ParentType extends Document | Block | Inline | Fragment = Document | Block | Inline | Fragment,
> = (
    node: NodeType,
    eat: (
        // @ts-expect-error
        children: NodeType['nodes'],
        context?: Partial<FormattingContext>
    ) => Promise<Array<ExpectedChildren>>,
    context: FormattingContext,
    parent: ParentType
) => Promise<Array<Result>> | Array<Result>;

/**
 * Map of keys to transform functions.
 */
export type GastToAstTransformMap<
    NodeType extends Block | Inline | Text = Block | Inline | Text,
    Result = unist.Node,
    ExpectedChildren = unist.Node,
    ParentType extends Document | Block | Inline | Fragment = Document | Block | Inline | Fragment,
> = {
    [key: string]: GastToAstTransformFn<NodeType, Result, ExpectedChildren, ParentType>;
};

/**
 * Convert a document to a unist AST tree.
 */
export async function gastToAst<Result extends unist.Parent>(
    document: Document | Fragment | Inline | Block,
    topLevelContext: FormattingContext,
    handlers: {
        transformInlines?: GastToAstTransformMap<Inline>;
        transformBlocks?: GastToAstTransformMap<Block>;
        transformText: GastToAstTransformFn<Text>;
    }
): Promise<Result> {
    async function transformBlock(
        block: Block,
        parent: Block | Document | Inline | Fragment,
        context: FormattingContext = topLevelContext
    ): Promise<unist.Node[]> {
        const transform = handlers.transformBlocks?.[block.type];
        if (!transform) {
            return [];
        }

        return transform(
            block,
            (children, ctx) => {
                return transformNodes(children, block, { ...context, ...ctx });
            },
            context,
            parent
        );
    }

    async function transformInline(
        inline: Inline,
        parent: Block | Document | Inline | Fragment,
        context: FormattingContext = topLevelContext
    ): Promise<unist.Node[]> {
        const transform = handlers.transformInlines?.[inline.type];
        if (!transform) {
            return [];
        }

        return transform(
            inline,
            (children, ctx) => {
                return transformNodes(children, inline, { ...context, ...ctx });
            },
            context,
            parent
        );
    }

    async function transformNode(
        node: Inline | Block | Text,
        parent: Block | Document | Inline | Fragment,
        context: FormattingContext = topLevelContext
    ): Promise<unist.Node[]> {
        switch (node.object) {
            case 'block':
                return transformBlock(node, parent, context);
            case 'inline':
                return transformInline(node, parent, context);
            case 'text':
                return handlers.transformText(node, async () => [], context, parent);
            default:
                assertNever(node);
        }
    }

    async function transformNodes(
        nodes: Fragment['nodes'] | Block['nodes'] | Document['nodes'] | Inline['nodes'],
        parent: Block | Document | Inline | Fragment,
        context: FormattingContext = topLevelContext
    ): Promise<unist.Node[]> {
        const content: unist.Node[] = [];
        for (const node of nodes) {
            const result = await transformNode(node, parent, context);
            content.push(...result);
        }
        return content;
    }

    return {
        type: 'root',
        children: await transformNodes(
            // @ts-expect-error
            Inline.isInline(document) || Block.isBlock(document) ? [document] : document.nodes,
            document
        ),
    } as Result;
}
